
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { HelmetProvider } from "react-helmet-async";
import App from "@/App";
import { AuthProvider } from "@/context/AuthContext";
import { ToastProvider } from "@/context/ToastContext";
import { Theme } from "@radix-ui/themes";
import "@radix-ui/themes/styles.css";
import "@/styles/global.css";
import "@/styles/index.css";
import { ErrorHandlerProvider } from "./context/ErrorHandlerContext";
import { CustomRollbarProvider } from "./providers/CustomRollbarProvider";
import "./utils/amplitude";
import { watchForNewBuild } from "@/utils/newBuild";
import { reloadForNewBuild } from "@/utils/lazyWithRetry";

// SUPERSEDED: swallowed every chunk failure (preventDefault) and reloaded
// nothing. Vite then resolves the import to `undefined`, lazyWithRetry() sees
// no error to retry, and React throws "Cannot read properties of undefined
// (reading 'default')" -- the error screen users hit after each deploy.
// window.addEventListener("vite:preloadError", (event) => {
//   event.preventDefault();
// });

// A file of the build this tab was opened on is gone: a new version has been
// deployed. Load it -- the user just sees the page they asked for. Only when
// the reload is actually happening is the error swallowed; otherwise (it just
// reloaded, so something is really broken) it reaches the ErrorBoundary.
window.addEventListener("vite:preloadError", (event) => {
  if (reloadForNewBuild()) event.preventDefault();
});
// Spot a new deployment before anything breaks: the next page change loads it.
watchForNewBuild();

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount, error: any) => {
        // Don't retry on rate limit errors (429) or auth errors (401, 403)
        if (error?.response?.status === 429) return false;
        if (error?.response?.status === 401) return false;
        if (error?.response?.status === 403) return false;
        // Retry other errors up to 1 time
        return failureCount < 1;
      },
    },
  },
});


createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <CustomRollbarProvider>
      {/* <StrictMode> */}
      <Theme appearance="light" accentColor="blue" scaling="95%">
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <ToastProvider>
              <ErrorHandlerProvider>
                <AuthProvider>
                  <App />
                </AuthProvider>
              </ErrorHandlerProvider>
            </ToastProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </BrowserRouter>
      </Theme>
    </CustomRollbarProvider>
  </HelmetProvider>,
);
